import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const description = productGrid.querySelector('.is-product-description');
      const viewDetails = productGrid.querySelector('.view-more-details__wrapper');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      const badgesRow = document.createElement('div');
      badgesRow.classList.add('view-product-title__badges');

      const newArrivalsBadge = productGrid.querySelector('.product-new-arrivals-badge--modal');
      if (newArrivalsBadge) {
        badgesRow.appendChild(newArrivalsBadge);
      }

      const bestSellersBadge = productGrid.querySelector('.product-best-sellers-badge--modal');
      if (bestSellersBadge) {
        badgesRow.appendChild(bestSellersBadge);
      }

      if (badgesRow.childElementCount > 0) {
        productHeader.appendChild(badgesRow);
      }

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (description) {
        productGrid.appendChild(description);
      }
      if (viewDetails) {
        productGrid.appendChild(viewDetails);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Position "View more details" above the Add to Cart button (buy buttons block) on desktop
    const viewDetails = productGrid.querySelector('.view-more-details__wrapper');
    const buyButtons = productGrid.querySelector('.buy-buttons-block');
    if (viewDetails && buyButtons && buyButtons.parentNode) {
      buyButtons.parentNode.insertBefore(viewDetails, buyButtons);
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.setupVerticalGalleryArrows(modalContent);
    }
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdateGallery, { signal: this.#abortController.signal });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Replaces the modal media gallery when the selected variant changes.
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdateGallery = (event) => {
    if (!(event.target instanceof HTMLElement) || !this.contains(event.target)) return;

    const html = event.detail.data.html;
    if (!html) return;

    const modalContent = this.querySelector('#quick-add-modal-content');
    if (!modalContent) return;

    const currentGallery = modalContent.querySelector('.product-information__media media-gallery');
    const newGallery =
      html.querySelector('.product-information__media media-gallery') ||
      html.querySelector('[data-product-grid-content] media-gallery') ||
      html.querySelector('media-gallery');

    if (!currentGallery || !newGallery) return;

    currentGallery.replaceWith(newGallery);

    this.setupVerticalGalleryArrows(modalContent);

    const mediaId = event.detail.resource?.featured_media?.id;
    if (!mediaId) return;

    requestAnimationFrame(() => {
      const gallery = modalContent.querySelector('.product-information__media');
      if (!gallery) return;

      const targetSlide = gallery.querySelector(`slideshow-slide[slide-id="${mediaId}"]`);
      if (targetSlide) {
        gallery.scrollTo({
          top: targetSlide.offsetTop,
          behavior: 'smooth',
        });
      }
    });
  };

  /**
   * Sets up up/down scroll arrows for the vertical media gallery on desktop
   * @param {Element} modalContent - The modal content element
   */
  setupVerticalGalleryArrows(modalContent) {
    const gallery = modalContent.querySelector('.product-information__media');
    if (!gallery) return;

    // Only set up arrows on desktop (window width >= 750px)
    if (window.innerWidth < 750) return;

    // Wrap the gallery in a relative container if not already wrapped
    const parent = gallery.parentElement;
    if (!parent) return;

    let wrapper = parent;
    if (!wrapper.classList || !wrapper.classList.contains('gallery-wrapper-relative')) {
      wrapper = document.createElement('div');
      wrapper.className = 'gallery-wrapper-relative';
      parent.insertBefore(wrapper, gallery);
      wrapper.appendChild(gallery);
    }

    // Remove existing arrows from the wrapper
    wrapper.querySelectorAll('.gallery-nav-arrow').forEach((arrow) => arrow.remove());

    // Create Up Arrow button
    const upArrow = document.createElement('button');
    upArrow.className = 'gallery-nav-arrow gallery-nav-arrow--up is-hidden';
    upArrow.type = 'button';
    upArrow.ariaLabel = 'Scroll up';
    upArrow.innerHTML = `
      <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="18 15 12 9 6 15"></polyline>
      </svg>
    `;

    // Create Down Arrow button
    const downArrow = document.createElement('button');
    downArrow.className = 'gallery-nav-arrow gallery-nav-arrow--down is-hidden';
    downArrow.type = 'button';
    downArrow.ariaLabel = 'Scroll down';
    downArrow.innerHTML = `
      <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round">
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    `;

    // Function to update arrow visibility based on scroll position
    const updateArrowVisibility = () => {
      const isScrollable = gallery.scrollHeight > gallery.clientHeight;
      if (!isScrollable) {
        upArrow.classList.add('is-hidden');
        downArrow.classList.add('is-hidden');
        return;
      }

      const isAtTop = gallery.scrollTop <= 5;
      const isAtBottom = gallery.scrollTop + gallery.clientHeight >= gallery.scrollHeight - 5;

      upArrow.classList.toggle('is-hidden', isAtTop);
      downArrow.classList.toggle('is-hidden', isAtBottom);
    };

    // Click event listeners to scroll one viewport height
    upArrow.addEventListener('click', () => {
      gallery.scrollBy({ top: -gallery.clientHeight * 0.8, behavior: 'smooth' });
    });

    downArrow.addEventListener('click', () => {
      gallery.scrollBy({ top: gallery.clientHeight * 0.8, behavior: 'smooth' });
    });

    // Observe scroll events on the gallery
    gallery.addEventListener('scroll', updateArrowVisibility);

    // Append arrows to the wrapper container
    wrapper.appendChild(upArrow);
    wrapper.appendChild(downArrow);

    // Scroll to initial slide on load if applicable
    const slideshow = gallery.querySelector('slideshow-component, slideshow');
    if (slideshow) {
      const initialIndex = parseInt(slideshow.getAttribute('initial-slide'), 10) || 0;
      if (initialIndex > 0) {
        const slides = gallery.querySelectorAll('slideshow-slide');
        const targetSlide = slides[initialIndex];
        if (targetSlide) {
          gallery.scrollTo({
            top: targetSlide.offsetTop,
            behavior: 'instant',
          });
        }
      }
    }

    // Initial check (use a small delay to ensure rendering and scroll heights are resolved)
    setTimeout(updateArrowVisibility, 100);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    VariantPicker.restorePortaledTooltips();

    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
